<template>
  <div class="confirm-view">
    <b-container v-if="!loading">
      <section class="confirm-view__section">
        <purchase-steps active_id="confirm" />
      </section>

      <section class="confirm-view__section confirm-view__section--success">
        <span class="confirm-view__badge">
          <vb-icon
            name="check-circle"
            :color="sass('success')"
            :size="48"
          />
        </span>

        <h1 class="confirm-view__title">Ordine completato con successo!</h1>
        <p class="confirm-view__text">La tua Gift Card è stata generata ed è pronta all'uso</p>
        <p v-if="redirect" class="confirm-view__text">
          Verrai riportato al tuo portale welfare tra
          <strong>{{ redirect_counter > 0 ? redirect_counter : 0 }}</strong> secondi:
          il codice è già nella tua email.
        </p>
        <p class="confirm-view__note">
          <strong>NB:</strong> Le card disponibili e i relativi servizi dipendono dalla
          configurazione del tuo partner welfare.
        </p>
      </section>

      <section class="confirm-view__section confirm-view__section--card">
        <order-recap />
      </section>

      <section class="confirm-view__section">
        <next-steps />
      </section>
    </b-container>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import { service_handler } from '@services';
  import { build_order } from '@livegift/resources';
  import PurchaseSteps from '@livegift/components/blocks/purchase-steps.vue';
  import OrderRecap from '@livegift/components/blocks/confirm/order-recap.vue';
  import NextSteps from '@livegift/components/blocks/confirm/next-steps.vue';

  // secondi prima di riportare l'utente al portale welfare
  const REDIRECT_SECONDS = 30;

  export default {
    name: 'confirm-view',

    components: {
      'purchase-steps': PurchaseSteps,
      'order-recap': OrderRecap,
      'next-steps': NextSteps,
    },

    props: {
      // coppia con cui gc_confirm.php rilegge l'ordine, dalla rotta
      confirm_code: {
        type: String,
        default: '',
      },
      gift_voucher_id: {
        type: String,
        default: '',
      },
      // se false e il partner welfare dichiara `orderConfirmedRedirectTo`,
      // l'utente viene riportato al suo portale (vedi try_redirect)
      ignore_redirect: {
        type: Boolean,
        default: true,
      },
    },

    data() {
      return {
        loading: true,
        // conto alla rovescia prima del rimbalzo al portale welfare
        redirect: false,
        redirect_counter: REDIRECT_SECONDS,
        redirect_interval: null,
      };
    },

    computed: {
      ...mapState('livegift', ['order']),
    },

    created() {
      if (!this.confirm_code || !this.gift_voucher_id) {
        this.$router.replace({ name: 'homepage' });
        return;
      }

      // l'ordine è emesso: l'intento d'acquisto non serve più e va azzerato subito,
      // o dal back del browser il checkout troverebbe ancora un importo da comprare
      // e la stessa card si potrebbe acquistare due volte. `order` resta finché la
      // pagina è a video, è quello che mostra: lo porta via il beforeDestroy
      this.$store.commit('livegift/reset_purchase');

      this.try_redirect();

      // subito dopo l'acquisto l'ordine è già nello store, montato dalla response
      // di `book`: non serve richiederlo, e così un intoppo di rete non nasconde
      // all'utente il codice che ha appena pagato
      if (this.order !== null) {
        this.loading = false;
        return;
      }

      // altrimenti si arriva da un link o da un refresh, che azzera lo store
      this.get_confirm(this.gift_voucher_id, this.confirm_code);
    },

    beforeDestroy() {
      this._clear_redirect();

      // uscendo si porta via anche l'ordine: era l'ultimo pezzo di questo acquisto
      // rimasto nello store, e chi tornasse qui col back lo rilegge da gc_confirm.
      // Il recap non ci si schianta pur leggendo `order`: viene distrutto in questa
      // stessa patch, prima che lo scheduler possa rieseguirne il render
      this.$store.commit('livegift/order', null);
    },

    methods: {
      /**
       * Rilettura di un ordine già emesso (gc_confirm.php).
       *
       * La response ha le stesse chiavi di quella di `book` — è una scelta
       * dichiarata del backend — quindi l'ordine si monta con lo stesso
       * `build_order`.
       *
       * @param {String} giftVoucherId id del voucher, dalla rotta
       * @param {String} confirmcode codice di conferma, dalla rotta
       */
      get_confirm(giftVoucherId, confirmcode) {
        service_handler(
          'confirm',
          { giftVoucherId, confirmcode },
          { modal: false },
        ).then(
          (success) => {
            this.$store.commit('livegift/order', build_order(success.data));

            this.loading = false;
          },
          (error) => { this._handle_confirm_error(error); },
        );
      },

      /**
       * Rimbalzo al portale welfare, quando l'integrazione lo prevede.
       *
       * Non è una cortesia: se il partner dichiara `orderConfirmedRedirectTo`
       * gc_checkout.php fa scadere il token cinque secondi dopo l'emissione
       * (permette un acquisto per volta), quindi lasciare l'utente qui vuol
       * dire lasciarlo su una sessione morta. Il conto alla rovescia gli dà il
       * tempo di copiare il codice prima di andarsene.
       *
       * Su /prenotazioni la prop è true e non scatta nulla: chi rilegge un
       * vecchio ordine non sta comprando niente.
       */
      try_redirect() {
        const redirect_to = this.get_config('integration.orderConfirmedRedirectTo', false);

        if (this.ignore_redirect || !redirect_to) return;

        this.redirect = true;

        this.redirect_interval = setInterval(() => {
          this.redirect_counter -= 1;

          if (this.redirect_counter <= 0) {
            // fermo il timer prima di navigare, così nessun tick resta orfano
            this._clear_redirect();
            window.location.href = redirect_to;
          }
        }, 1000);
      },

      _clear_redirect() {
        clearInterval(this.redirect_interval);
        this.redirect_interval = null;
      },

      /**
       * Ordine non leggibile: senza dati non c'è una conferma da mostrare, quindi
       * si torna alla home. Il backend stesso risponde `routerPush: 'homepage'`.
       * @param {Object} error errore del service_handler
       */
      _handle_confirm_error(error) {
        this.log('[confirm] - error');
        this.log(error.response);
        this.$router.replace({ name: 'homepage' });
      },
    },
  };
</script>

<style lang="scss" scoped>

  .confirm-view {

    // il contenuto della conferma è più stretto delle altre pagine del site
    .container {

      @include media-breakpoint-up(lg) {
        max-width: 810px + $grid-gutter-width;
      }
    }

    &__section {
      margin-top: $grid-gutter-width * 2;

      &:last-child {
        margin-bottom: $grid-gutter-width * 3;
      }
    }

    &__section--success {
      text-align: center;
    }

    // cerchio verde attorno al segno di spunta
    &__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 70px;
      height: 70px;
      background-color: #dcfce7;
      border-radius: 50%;
    }

    &__title {
      margin: ($grid-gutter-width / 2) 0 0;
      font-size: 30px;
      font-weight: $vb-font-weight-semibold;
      color: $gray-900;
      line-height: normal;

      @include media-breakpoint-up(xl) {
        font-size: $font-size-base * 2.25;
      }
    }

    &__text {
      margin: ($grid-gutter-width / 2) 0 0;
      font-size: $font-size-lg;
      color: $gray-800;

      @include media-breakpoint-up(xl) {
        font-size: $font-size-xl;
      }
    }

    &__note {
      margin: ($grid-gutter-width / 3) 0 0;
      font-size: $font-size-sm;
      color: $gray-700;
    }

    /**
     * In stampa il foglio contiene la sola gift card: header, prefooter e footer si
     * nascondono da sé (vedi i loro @media print), qui restano da spegnere le altre
     * sezioni della pagina. `print-color-adjust` serve a non perdere la testata blu
     * e il riquadro del codice, che i browser scarterebbero come sfondi.
     */
    @media print {

      &__section {
        display: none;
      }

      &__section--card {
        display: block;
        margin: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  }
</style>
